import i18n from '@risksmart-app/i18n/src/i18n';
import type { IndicatorRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIndicatorsQuery,
  Indicator_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIndicatorsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcIndicatorsToGraphQL } from 'src/hooks/queries/indicator/useGetIndicatorRegister';
import { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useLabelledFields as useIndicatorLabelledFields } from 'src/pages/indicators/useLabelledFields';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { getFriendlyId } from '@/utils/friendlyId';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  type IndicatorRowSlice,
  indicatorSideCategoryGetters,
  indicatorSideFieldConfig,
  prefixedLabel,
  resolveIndicatorSideRow,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type IndicatorRiskRow = TableRecord &
  IndicatorRowSlice & {
    Id: string;
    RiskId: string;
    RiskFriendlyId: string;
    RiskTitle: string;
    RiskTier: string;
    RiskStatus: string;
    RiskTreatment: string;
  };

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Indicator],
  documentNode: GetIndicatorsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.indicator.register.queryOptions();
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: IndicatorRegisterResponse) =>
        mapTrpcIndicatorsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Indicator_Bool_Exp>(Parent_Type_Enum.Indicator),
  }),
  useTablePropsHook: (
    data: GetIndicatorsQuery | undefined,
    options: StatefulTableOptions<IndicatorRiskRow>
  ) => {
    const fields = useMemo<TableFields<IndicatorRiskRow>>(
      () => ({
        RiskFriendlyId: simpleField(
          prefixedLabel('risk_one', i18n.t('columns.id'))
        ),
        RiskTitle: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_name'))
        ),
        RiskTier: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier'))
        ),
        RiskStatus: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_status'))
        ),
        RiskTreatment: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment'))
        ),
        ...indicatorSideFieldConfig<IndicatorRiskRow>(),
      }),
      []
    );

    const indicatorsLabelled = useIndicatorLabelledFields(data?.indicator);
    const { getByValue: commonLookup } = useCommonLookupLazy();
    const { data: riskData } = useGetRisksFlat({ queryArgs: {} });

    const joinedData = useMemo<IndicatorRiskRow[] | undefined>(() => {
      if (!indicatorsLabelled || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return indicatorsLabelled.flatMap((indicator) =>
        (indicator.parents ?? [])
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${indicator.Id}`,
              RiskId: parent.parent!.Id,
              RiskFriendlyId: risk?.SequentialId
                ? getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)
                : '',
              // Partner title falls back to '' rather than a raw-joined value
              // from the indicator register — the register eager-loads
              // parent.risk.Title via a SQL join that is not re-filtered by
              // the partner entity's permission scope, so using it here
              // would leak titles the user can't otherwise see.
              RiskTitle: risk?.Title ?? '',
              RiskTier: commonLookup('tiers', risk?.Tier)?.label ?? '',
              RiskStatus: commonLookup('statuses', risk?.Status)?.label ?? '',
              RiskTreatment:
                commonLookup('treatments', risk?.Treatment)?.label ?? '',
              ...resolveIndicatorSideRow(indicator),
            };
          })
      );
    }, [indicatorsLabelled, riskData?.risk, commonLookup]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_indicator_other'),
      options
    );
  },
  entityNamePlural: 'risk_indicator_other',
  entityNameSingular: 'risk_indicator_one',
  fields: 'riskIndicators.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskFriendlyId',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('columns.id')),
      categoryGetter: (item: IndicatorRiskRow) =>
        item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_name')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_status')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment')),
      categoryGetter: (item: IndicatorRiskRow) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    ...indicatorSideCategoryGetters<IndicatorRiskRow>(),
  ],
});
